






































































import { computed, defineComponent, PropType } from "@vue/composition-api";
import { ContentFileThumbnailType } from "@scrile/api-provider/dist/api/ContentFilesProvider";
import { ContentSet } from "@scrile/api-provider/dist/api/ContentSetProvider";
import { UserPublicData } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import { ContentFeed, ContentFeedTypeEnum } from "@scrile/api-provider/dist/projects/scrileConnect/ContentFeedProvider";
import { checkAuthorities } from "@scrile/tools/dist/lib/SecurityHelpers";
import { parseISO } from "@scrile/tools/dist/lib/TimeHelpers";
import useCoverThumbnail from "@/hooks/useCoverThumbnail";
import { getFeedParticipants } from "@/lib/contentFeedHelper";
import { getFilesIcon } from "@/lib/fileHelpers";
import { format, getNow } from "@/lib/time";
import config from "@/config";
import AvatarsGroup from "@/components/AvatarsGroup";
import BaseIcon from "@/components/Base/BaseIcon";
import BaseChip from "@/components/BaseChip";
import BaseImage from "@/components/BaseImage";
import ContentCover from "@/components/ContentCover";

function isContentFeed(f: ContentFeed | ContentSet): f is ContentFeed {
  return "files" in f;
}
export default defineComponent({
  name: "ContentFeedCover",
  components: { AvatarsGroup, BaseChip, BaseIcon, ContentCover, BaseImage },
  props: {
    showNotSafeForWork: { type: Boolean, default: false },
    postData: { type: Object as PropType<ContentFeed | ContentSet>, required: true },
    small: { type: Boolean, default: false },
    hideIcon: { type: Boolean, default: false },
    authUserId: { type: String, default: "" },
    currentAuthorities: { type: Array as PropType<string[]>, default: () => [] },
    enablePinPost: { type: Boolean, default: true },
    showParticipants: { type: Boolean, default: false },
  },
  setup(props) {
    const sfwThumbnail = computed(() =>
      isContentFeed(props.postData)
        ? props.postData.files.find(f => f.thumbnailType === ContentFileThumbnailType.SFW)
        : props.postData.cover
    );

    const nsfwThumbnail = computed(() =>
      isContentFeed(props.postData)
        ? props.postData.files.find(f => f.thumbnailType === ContentFileThumbnailType.NSFW)
        : props.postData.cover
    );

    const coverFile = computed(() => (props.showNotSafeForWork ? nsfwThumbnail.value : sfwThumbnail.value));

    const { coverSrc } = useCoverThumbnail(coverFile);

    const isAuthor = computed(() => props.authUserId === props.postData.userId);
    const showErrorIcon = computed(() => props.postData.processingStatus === "ERROR");
    const showProcessingIcon = computed(() => ["NEW", "CONVERTING"].indexOf(props.postData.processingStatus) > -1);
    const showScheduledIcon = computed(
      () => props.postData.scheduleTime && parseISO(props.postData.scheduleTime).getTime() >= getNow().getTime()
    );
    const scheduledText = computed(() =>
      props.postData.scheduleTime ? format(parseISO(props.postData.scheduleTime), "MM.dd.yy") : ""
    );
    const isNotPublished = computed(() => !props.postData.publishTime);
    const allowPinPosts = computed(() => config.featurePinPost);

    const showGradient = computed(
      () =>
        isAuthor.value &&
        (showErrorIcon.value || showProcessingIcon.value || showScheduledIcon.value || isNotPublished.value)
    );

    const title = computed(() => props.postData.title);
    const showDarkGradient = computed(() => props.postData.type === ContentFeedTypeEnum.RICH_TEXT && !!coverFile.value);

    const icon = computed(() => {
      if (props.postData.type === ContentFeedTypeEnum.EVENT) {
        return "msr-event";
      } else if (props.postData.type === ContentFeedTypeEnum.STORY) {
        return "sci-story";
      } else if (props.postData.type === ContentFeedTypeEnum.RICH_TEXT) {
        return "sci-text-post";
      } else if (props.postData.type === ContentFeedTypeEnum.MEDIA && isContentFeed(props.postData)) {
        return getFilesIcon(props.postData.files);
      } else {
        return "sci-file-multiple";
      }
    });

    const mainIcon = computed(() => {
      if (showProcessingIcon.value) return "sci-processing";
      if (showScheduledIcon.value) return "sci-watch";
      return "msr-visibility_off-filled";
    });

    const isPinned = computed(() => allowPinPosts.value && isContentFeed(props.postData) && !!props.postData.pinTime);
    const showLocked = computed(() =>
      isContentFeed(props.postData) &&
      checkAuthorities(props.currentAuthorities, isAuthor ? ["updateMyContentSet"] : ["updateAnyContentSet"])
        ? props.postData.subscriptionLevelId !== "0" || props.postData.pricePackageId !== "0"
        : false
    );

    const participants = computed<UserPublicData[]>(() => {
      if (!props.showParticipants || !isContentFeed(props.postData) || !props.postData.participants) return [];
      return getFeedParticipants(props.postData);
    });

    return {
      showErrorIcon,
      showProcessingIcon,
      showLocked,
      showGradient,
      showScheduledIcon,
      coverSrc,
      icon,
      isPinned,
      mainIcon,
      scheduledText,
      title,
      showDarkGradient,
      participants,
    };
  },
});
